import * as React from 'react';

import {GridList} from '../grid-list/grid-list';

import {TwoPaneSelectList} from './two-pane-select-list';

import styles from './categorized-select-list.module.css';

export type Category<C> = {
    id: string;
    title: string;
    subtitle?: string;
    label?: string;
    collection: C[] | null | undefined;
    isLoading?: boolean;
    error?: string | null | undefined;
    emptyStateComponent?: React.ReactNode;
    customFillRule?: string;
};

type Props<C> = {
    categorizedLists: Category<C>[] | null | undefined;
    renderItem: (item: C, category: Category<C>) => React.ReactElement;
    isLoading?: boolean;
    isFiltering?: boolean;
    initialSelectedCategoryId?: string;
    onSelectCategory?: (category: Category<C>) => void;
};

export function CategorizedSelectList<C>(props: Props<C>) {
    const {categorizedLists, onSelectCategory, initialSelectedCategoryId} = props;

    const [selectedCategoryId, setSelectedCategoryId] = React.useState<string | null | undefined>(
        null
    );

    React.useEffect(() => {
        if (selectedCategoryId || !categorizedLists || categorizedLists.length === 0) {
            return;
        }

        const category =
            categorizedLists.find((list) => list.id === initialSelectedCategoryId) ??
            categorizedLists[0];

        // @ts-expect-error
        setSelectedCategoryId(category.id);
        if (onSelectCategory) {
            // @ts-expect-error
            onSelectCategory(category);
        }
    }, [categorizedLists, selectedCategoryId, onSelectCategory, initialSelectedCategoryId]);

    const selectedCategory =
        categorizedLists && categorizedLists.find((list) => selectedCategoryId === list.id);

    return (
        <TwoPaneSelectList
            categorizedLists={categorizedLists}
            selectedCategory={selectedCategory}
            onSelectCategory={(category: Category<C>) => {
                setSelectedCategoryId(category.id);
                if (props.onSelectCategory) {
                    props.onSelectCategory(category);
                }
            }}
            rightPanelContent={
                <div className={styles['list-item-selection']}>
                    <GridList
                        customFillRule={
                            selectedCategory && selectedCategory.customFillRule
                                ? selectedCategory.customFillRule
                                : undefined
                        }
                        numColumns={2}
                        isLoading={
                            (selectedCategory && selectedCategory.isLoading) || props.isLoading
                        }
                        loadingState={{
                            itemHeight: 300,
                            numItems: 3,
                            itemBackgroundColor: 'offWhiteDk',
                        }}
                        error={selectedCategory && selectedCategory.error}
                        isFiltering={props.isFiltering}
                        emptyStateComponent={
                            selectedCategory && selectedCategory.emptyStateComponent
                        }
                    >
                        {(selectedCategory &&
                            selectedCategory.collection &&
                            selectedCategory.collection.map((item) =>
                                props.renderItem(item, selectedCategory)
                            )) ||
                            null}
                    </GridList>
                </div>
            }
        />
    );
}
